import { useState } from 'react'
import { DataGrid, GridPaginationModel, GridColDef, DataGridProps } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { PrometheusAlert } from '../types'
import {
  translateAlertPhase,
  getAlertTimestamp,
  getHumanizedAlertValue,
  getUniqueAlerts,
  translateAlertRuleName,
  translateAlertSeverity,
  getAlertValue,
  getAlertThresholdValue,
  getHumanizedAlertThresholdValue,
  formatAlertValue,
  formatAlertThresholdValue,
  getAlertStatus
} from '../utils/alert'
import { humanizeTimestamp } from '../utils/date'
import AlertRuleLabel from './AlertRuleLabel'
import AlertSeverityChip from './AlertSeverityChip'
import AlertSummary from './AlertSummary'
import { Stack } from '@mui/material'
import AlertStatusChip from './AlertStatusChip'

interface AlertDataGridProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  alerts: PrometheusAlert[]

  /**
   * Indicates if only unique alerts are displayed.
   */
  showUnique?: boolean
}

export default function AlertDataGrid({ alerts, showUnique = true }: AlertDataGridProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0
  })

  /**
   * The columns.
   */
  const columns: GridColDef<PrometheusAlert>[] = [
    {
      field: 'activeAt',
      headerName: t('time'),
      type: 'date',
      width: 200,
      editable: false,
      valueGetter: (_, row) => getAlertTimestamp(row),
      valueFormatter: (value: Date | null) => {
        if (value) {
          return humanizeTimestamp(value)
        } else {
          return null
        }
      }
    },
    {
      field: 'labels.severity',
      headerName: t('severity'),
      type: 'string',
      width: 150,
      editable: false,
      valueGetter: (_, row) => translateAlertSeverity(row.labels.severity),
      renderCell: ({ row }) => (
        <AlertSeverityChip severity={row.labels.severity} variant="outlined" size="small" />
      )
    },
    {
      field: 'labels.alertname',
      headerName: t('name'),
      type: 'string',
      width: 300,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.labels.alertname),
      renderCell: ({ row }) => <AlertRuleLabel alertRule={row.labels.alertname} />
    },
    // {
    //   field: 'labels.name',
    //   headerName: t('phase'),
    //   type: 'string',
    //   width: 200,
    //   editable: false,
    //   valueGetter: (_, row) => translateAlertPhase(row)
    // },
    {
      field: 'summary',
      headerName: t('summary'),
      type: 'string',
      flex: 1,
      minWidth: 200,
      editable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
          <AlertSummary alert={row} translationKey="summary" />
        </Stack>
      )
    },
    // {
    //   field: 'status',
    //   headerName: t('status'),
    //   type: 'string',
    //   width: 150,
    //   editable: false,
    //   valueGetter: (_, row) => getAlertStatus(row),
    //   renderCell: ({ row }) => <AlertStatusChip alert={row} variant="outlined" size="small" />
    // },
    {
      field: 'value',
      headerName: t('alert:alertingValue'),
      type: 'number',
      align: 'right',
      width: 150,
      editable: false,
      valueGetter: (_, row) => getAlertValue(row),
      valueFormatter: (_, row) => formatAlertValue(row)
    },
    {
      field: 'labels.param1',
      headerName: t('threshold'),
      type: 'number',
      align: 'right',
      width: 150,
      editable: false,
      valueGetter: (_, row) => getAlertThresholdValue(row),
      valueFormatter: (_, row) => formatAlertThresholdValue(row)
    }
  ]

  /**
   * The unique alerts by alert name and phase.
   */
  const uniqueAlerts = getUniqueAlerts(alerts)

  /**
   * The shown alerts.
   */
  const shownAlerts = showUnique ? uniqueAlerts : alerts

  /**
   * Get the row ID.
   */
  const handleRowId = (alert: PrometheusAlert) => {
    let rowId = `${alert.labels?.device ?? alert.labels?.ruuvitag ?? 'device'}-${
      alert.labels.alertname
    }-${alert.labels.name}`

    if (alert?.startsAt) {
      rowId = `${rowId}-${new Date(alert.startsAt).getTime()}`
    } else if (alert?.activeAt) {
      rowId += `${rowId}-${new Date(alert.activeAt).getTime()}`
    }

    return rowId
  }

  return (
    <DataGrid
      rows={shownAlerts}
      columns={columns}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      disableRowSelectionOnClick
      getRowId={handleRowId}
      sx={{ border: 0 }}
    />
  )
}
